import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi, type PartnerLandingInput } from '../../api/reports.js';
import { ApiError } from '../../api/client.js';
import { Field } from '../../components/ui.js';
import { ReportBody } from './ReportPage.js';

/**
 * 파트너 랜딩수정 — 정의 기반 리포트 partner-landing 위에 "파트너 조건
 * 추가하기" 버튼과 줄마다 "수정" 버튼, 추가/수정 모달을 얹은 화면.
 *
 * 원본: partner_manage.php(목록) + partner_manage_update.php(추가·수정 폼)
 * 원본은 목록 위의 "파트너 조건 추가하기" 링크(partner_manage.php:234)와 각 줄의
 * 수정 링크(:297)가 **같은 폼**을 thickbox iframe 으로 띄웠다 - seq 가 있으면
 * 그 값으로 채워진 수정 폼, 없으면 빈 추가 폼이었다. 저장하면
 * `parent.document.location.reload()` 로 부모 창을 통째로 새로고침했다.
 * 여기서는 같은 화면 안에서 같은 모달을 추가/수정 두 자리에 쓰고, 저장 뒤
 * 표만 다시 조회한다 (zaiko-stock · offline-cate 와 같은 방식).
 *
 * 폼의 칸·순서·안내문구는 원본 partner_manage_update.php 를 그대로 따랐다.
 *
 * 원본에는 이 폼에 "삭제하기"(mode=delexe)도 있었다 - 이번 요청은 추가·수정
 * 까지라 삭제는 옮기지 않았다.
 */

/* 원본 partner_manage_config.php 의 배열 그대로. 이 테이블만 'Y'/'N' 이 아니라
 * 't'/'f' 를 쓴다 - 서버 shared-labels.ts 의 PARTNER_* 와 같은 값이어야 한다. */
const IF_MIDDLE = [
  { value: 'same', label: '같음(정확히)' },
  { value: 'intext', label: '포함' },
  { value: 'instart', label: '으로 시작' },
  { value: 'inend', label: '으로 끝' },
];
const ISUSE = [
  { value: 't', label: '사용' },
  { value: 'f', label: '미사용' },
];
const IF_404 = [
  { value: 't', label: '유입정지 사용' },
  { value: 'f', label: '유입정지 미사용' },
];
const IF_AUTH = [
  { value: 't', label: '인증완료 처리' },
  { value: 'f', label: '인증필요' },
];
const IF_ADCODE = [
  { value: 't', label: '마케팅용도메인용' },
  { value: 'f', label: '미사용' },
];

/** 행(표에서 받은 값)을 폼 초기값으로 바꾼다. 없는 칸은 빈 문자열이다. */
function toForm(row: Record<string, unknown>): PartnerLandingInput {
  const s = (key: string) => (row[key] === null || row[key] === undefined ? '' : String(row[key]));
  // 라디오는 값이 비어 있는 행이 실제로 있다(if_404·if_auth·if_adcode). 원본 폼도
  // 그때는 아무것도 안 골라진 채로 떴는데, 그대로 저장하면 빈 값이 다시 들어간다.
  // 여기서는 '미사용'(f) 쪽으로 미리 골라둔다.
  const tf = (key: string) => (s(key) === 't' ? 't' : 'f');
  return {
    if_name: s('if_name'),
    partner_id: s('partner_id'),
    if_middle: s('if_middle') || 'same',
    if_value: s('if_value'),
    isuse: s('isuse') === 'f' ? 'f' : 't',
    _utm_source: s('_utm_source'),
    _utm_medium: s('_utm_medium'),
    _utm_campaign: s('_utm_campaign'),
    _utm_content: s('_utm_content'),
    if_bnid: s('if_bnid'),
    if_bnid_url: s('if_bnid_url'),
    doneurl: s('doneurl'),
    if_404: tf('if_404'),
    if_auth: tf('if_auth'),
    if_adcode: tf('if_adcode'),
    if_addurl: s('if_addurl'),
    byalign: Number(s('byalign')) || 0,
  };
}

/**
 * toForm({}) 을 그대로 쓴다 - row 가 비어 있으면 s() 는 모든 칸을 '' 로 읽고,
 * toForm 의 기본값 규칙(if_middle→'same', isuse→'t', if_404/if_auth/if_adcode→'f')
 * 이 그대로 적용된다. 원본이 새 폼에서 라디오를 아무것도 안 골라준 채 두던
 * 것과 달리, 처음부터 유효한 값이 골라져 있게 한다.
 */
const EMPTY_FORM: PartnerLandingInput = toForm({});

export function PartnerLandingPage() {
  // seq 가 null 이면 추가 모달이다.
  const [editing, setEditing] = useState<{ seq: number | null; form: PartnerLandingInput } | null>(null);

  return (
    <>
      <ReportBody
        reportId="partner-landing"
        headerExtra={
          <div className="toolbar">
            {/* 원본 partner_manage.php:234 "파트너 조건 추가하기" 링크 자리. */}
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setEditing({ seq: null, form: EMPTY_FORM })}
            >
              파트너 조건 추가하기
            </button>
          </div>
        }
        extraColumn={{
          label: '수정',
          render: (row) => (
            <button
              type="button"
              className="btn btn--ghost btn--sm partner-landing-edit"
              onClick={() => setEditing({ seq: Number(row.seq), form: toForm(row) })}
            >
              수정
            </button>
          ),
        }}
      />

      {editing && (
        <EditModal
          seq={editing.seq}
          initial={editing.form}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function EditModal({
  seq,
  initial,
  onClose,
}: {
  /** null 이면 추가 모달이다 (원본 partner_manage_update.php 의 mode=add). */
  seq: number | null;
  initial: PartnerLandingInput;
  onClose: () => void;
}) {
  const isNew = seq === null;
  const qc = useQueryClient();
  const [form, setForm] = useState<PartnerLandingInput>(initial);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: () =>
      isNew ? reportsApi.createPartnerLanding(form) : reportsApi.updatePartnerLanding(seq, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['report', 'partner-landing'] });
      onClose();
    },
  });

  function set<K extends keyof PartnerLandingInput>(key: K, value: PartnerLandingInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /** 원본이 alert 로 막던 필수 세 칸 (partner_manage_update.php:303-321). */
  const valid =
    form.if_name.trim() !== '' && form.partner_id.trim() !== '' && form.if_middle !== '';

  function radios(
    label: string,
    key: 'isuse' | 'if_404' | 'if_auth' | 'if_adcode',
    options: { value: string; label: string }[],
    help: string,
  ) {
    return (
      <div className="partner-landing-radio-field">
        <span className="field__label">{label}</span>
        <div
          className="partner-landing-radio-group"
          role="radiogroup"
          aria-label={label}
        >
          {options.map((o) => (
            <label
              key={o.value}
              className={form[key] === o.value
                ? 'partner-landing-radio partner-landing-radio--checked'
                : 'partner-landing-radio'}
            >
              <input
                type="radio"
                name={`${key}-${seq}`}
                value={o.value}
                checked={form[key] === o.value}
                onChange={() => set(key, o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
        {help && <small>{help}</small>}
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel partner-landing-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`partner-landing-title-${seq ?? 'new'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>

        <header className="partner-landing-modal__head">
          <div>
            <span>LANDING RULE · {isNew ? '신규' : `#${seq}`}</span>
            <h2 className="modal-title" id={`partner-landing-title-${seq ?? 'new'}`}>
              {isNew ? '파트너 조건 추가' : '파트너 조건 수정'}
            </h2>
            <p>유입 조건부터 추적값과 도착 경로까지 단계별로 확인하세요.</p>
          </div>
          <span className={form.isuse === 't'
            ? 'partner-landing-status__badge partner-landing-status__badge--active'
            : 'partner-landing-status__badge partner-landing-status__badge--inactive'}>
            <i />{form.isuse === 't' ? '사용 중' : '미사용'}
          </span>
        </header>

        <div className="partner-landing-modal__summary" aria-label="현재 조건 요약">
          <div><span>조건명</span><strong>{form.if_name || '입력 필요'}</strong></div>
          <div><span>파트너 ID</span><strong>{form.partner_id || '입력 필요'}</strong></div>
          <div><span>우선순위</span><strong>{form.byalign || '마지막'}</strong></div>
        </div>

        <form
          className="partner-landing-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <section className="partner-landing-form__section">
            <header><span>01</span><div><h3>기본 조건</h3><p>어떤 파트너 유입에 이 규칙을 적용할지 지정합니다.</p></div></header>
            <div className="partner-landing-form__grid partner-landing-form__grid--core">
              <Field label="이름(조건명)">
                <input
                  className="input"
                  aria-label="이름(조건명)"
                  value={form.if_name}
                  placeholder="예: 엑소 포워딩, 신규 광고 조건"
                  onChange={(e) => set('if_name', e.target.value)}
                />
              </Field>
              {radios('사용여부', 'isuse', ISUSE, '')}
              <Field label="파트너아이디" full as="div">
                <div className="partner-landing-condition-editor">
                  <input
                    className="input"
                    aria-label="파트너아이디"
                    value={form.partner_id}
                    placeholder="bnn_"
                    onChange={(e) => set('partner_id', e.target.value)}
                  />
                  <select
                    className="input"
                    aria-label="조건식"
                    value={form.if_middle}
                    onChange={(e) => set('if_middle', e.target.value)}
                  >
                    {IF_MIDDLE.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <span className="hint">파트너 ID와 비교할 규칙입니다. 예: bnn_, bnn_exo_click</span>
              </Field>
              <Field label="간단메모" full>
                <input
                  className="input"
                  aria-label="간단메모"
                  value={form.if_value}
                  placeholder="이 조건의 용도나 담당 캠페인을 기록하세요"
                  onChange={(e) => set('if_value', e.target.value)}
                />
              </Field>
            </div>
          </section>

          <section className="partner-landing-form__section">
            <header><span>02</span><div><h3>GA4 추적 설정</h3><p>비워두면 시스템 기본 추적값을 사용합니다.</p></div></header>
            <div className="partner-landing-form__grid partner-landing-form__grid--tracking">
              <Field label="GA4(source)">
                <input className="input" aria-label="GA4(source)" value={form._utm_source} placeholder="partner"
                  onChange={(e) => set('_utm_source', e.target.value)} />
                <span className="hint">유입 출처</span>
              </Field>
              <Field label="GA4(medium)">
                <input className="input" aria-label="GA4(medium)" value={form._utm_medium} placeholder="exoclick"
                  onChange={(e) => set('_utm_medium', e.target.value)} />
                <span className="hint">유입 매체</span>
              </Field>
              <Field label="GA4(campaign)">
                <input className="input" aria-label="GA4(campaign)" value={form._utm_campaign} placeholder="exo_banner"
                  onChange={(e) => set('_utm_campaign', e.target.value)} />
                <span className="hint">캠페인 이름</span>
              </Field>
              <Field label="GA4(content)">
                <input className="input" aria-label="GA4(content)" value={form._utm_content} placeholder="onahol_banner"
                  onChange={(e) => set('_utm_content', e.target.value)} />
                <span className="hint">광고 소재 구분</span>
              </Field>
            </div>
          </section>

          <section className="partner-landing-form__section">
            <header><span>03</span><div><h3>이동 경로</h3><p>BNID 분기와 최종 도착 URL, 추가 파라미터를 설정합니다.</p></div></header>
            <div className="partner-landing-form__grid partner-landing-form__grid--redirect">
              <Field label="조건(bnid)">
                <input className="input" aria-label="조건(bnid)" value={form.if_bnid} placeholder="예: 1"
                  onChange={(e) => set('if_bnid', e.target.value)} />
                <span className="hint">bnid 비교값</span>
              </Field>
              <Field label="결과(bnid) URL">
                <input className="input" aria-label="결과(bnid) URL" value={form.if_bnid_url} placeholder="https://do.bananam.kr/"
                  onChange={(e) => set('if_bnid_url', e.target.value)} />
                <span className="hint">bnid가 일치할 때 이동할 URL</span>
              </Field>
              <Field label="도착URL" full>
                <input className="input" aria-label="도착URL" value={form.doneurl}
                  placeholder="https://www.bananamall.co.kr/shop/view.php?index_no=123123"
                  onChange={(e) => set('doneurl', e.target.value)} />
                <span className="hint">비워두면 메인 화면으로 이동합니다.</span>
              </Field>
              <Field label="추가 파라미터" full>
                <input className="input" aria-label="추가 파라미터" value={form.if_addurl} placeholder="addpara=exoclick"
                  onChange={(e) => set('if_addurl', e.target.value)} />
                <span className="hint">변수명으로 시작하고 맨 앞의 &amp;는 제외합니다.</span>
              </Field>
            </div>
          </section>

          <section className="partner-landing-form__section">
            <header><span>04</span><div><h3>처리 옵션</h3><p>유입 차단, 인증, 서브도메인 처리와 실행 순서를 결정합니다.</p></div></header>
            <div className="partner-landing-form__grid partner-landing-form__grid--settings">
              {radios('404설정', 'if_404', IF_404, '페이지 없음으로 처리할지 선택합니다.')}
              {radios('성인인증 설정', 'if_auth', IF_AUTH, '성인인증 완료 상태로 처리할지 선택합니다.')}
              {radios('랜덤서브도메인 사용여부', 'if_adcode', IF_ADCODE,
                'slnk에서 전달되는 마케팅용 서브도메인을 처리합니다.')}
              <Field label="적용우선순위">
                <input
                  className="input"
                  type="number"
                  min={0}
                  aria-label="적용우선순위"
                  value={form.byalign}
                  placeholder="10"
                  onChange={(e) => set('byalign', Number(e.target.value) || 0)}
                />
                <span className="hint">낮을수록 먼저 실행되며 0은 마지막에 적용됩니다.</span>
              </Field>
            </div>
            <p className="partner-landing-auto-note">
              <span aria-hidden="true">↻</span>
              적용수와 적용일은 자동 집계값이며 이 화면에서는 변경하지 않습니다.
            </p>
          </section>

          {save.isError && (
            <div className="qty-msg qty-msg--error partner-landing-form__error" role="alert">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <footer className="partner-landing-modal__footer">
            <div>
              <strong>
                {!valid
                  ? '필수 항목을 확인하세요'
                  : isNew
                    ? '아래 버튼으로 새 조건을 등록합니다'
                    : dirty
                      ? '저장하지 않은 변경사항이 있습니다'
                      : '현재 저장된 값입니다'}
              </strong>
              <span>조건명과 파트너아이디는 필수입니다.</span>
            </div>
            <div>
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                취소
              </button>
              <button type="submit" className="btn btn--primary" disabled={!valid || save.isPending}>
                {save.isPending ? '저장 중…' : isNew ? '추가하기' : '수정하기'}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}
